import { LOCAL_STORAGE_KEY_JWT, LOCAL_STORAGE_KEY_POST_LOGIN_REDIRECT } from '@/api'
import GoogleIcon from '@/assets/icons/google.svg?react'
import { DASHBOARD_URL } from '@/env'
import { useSessionProvider } from '@/features/authorization/components/SessionProvider'
import { isSafeRedirectPath } from '@/features/authorization/hooks/useAuthComplete'
import { Button, Input, Text } from '@/ui-kit'
import { useLocalStorage } from '@/utils/useLocalStorage'
import { Trans, useLingui } from '@lingui/react/macro'
import { useGoogleLogin } from '@react-oauth/google'
import { AxiosError } from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

export const LoginPage = () => {
  const { t } = useLingui()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [isEmailPristine, setIsEmailPristine] = useState(true)
  const [isEmailLoading, setIsEmailLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [emailSent, setEmailSent] = useState(false)
  const { signInWithMagicLink } = useSessionProvider()
  const [searchParams] = useSearchParams()
  const inviteCode = searchParams.get('invite')
  const isSignUp = !!inviteCode
  const redirectParam = searchParams.get('redirect')

  const [, setSavedRedirect] = useLocalStorage(LOCAL_STORAGE_KEY_POST_LOGIN_REDIRECT, '')

  useEffect(() => {
    if (redirectParam && isSafeRedirectPath(redirectParam)) {
      setSavedRedirect(redirectParam)
    }
  }, [redirectParam, setSavedRedirect])

  const [jwt] = useLocalStorage(LOCAL_STORAGE_KEY_JWT, '')

  const [errorMessage, setErrorMessage] = useState('')

  const pattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
  const isEmailValid = pattern.test(email)

  const location = useLocation()

  useEffect(() => {
    const params = location.hash.slice(1, location.hash.length).split('&')
    const error =
      params.map((param) => param.split('=')).find((paramArray) => paramArray[0] === 'error_description')?.[1] || ''

    if (error) {
      setErrorMessage(error.replaceAll('+', ' '))
    }
  }, [])

  const isLogged = jwt
  //const navigate = useNavigate()
  useEffect(() => {
    if (isLogged) {
      // navigate(routes.dashboard.path)
    }
  }, [isLogged])

  const handleEmailLogin = useCallback(
    async (emailValue?: string) => {
      setIsEmailPristine(false)
      setErrorMessage('')

      if (!emailValue || !isEmailValid) {
        return
      }

      setIsEmailLoading(true)
      try {
        await signInWithMagicLink(emailValue)
        setEmailSent(true)
      } catch (err) {
        const axiosError = err as AxiosError
        if (axiosError.response?.status === 429) {
          setErrorMessage('Too many requests. Please try again in an hour.')
        } else {
          setErrorMessage(axiosError.message)
        }
      } finally {
        setIsEmailLoading(false)
      }
    },
    [signInWithMagicLink, isEmailValid]
  )

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'redirect',
    redirect_uri: `${DASHBOARD_URL}/auth/google`,
    state: inviteCode || undefined
  })

  return (
    <>
      {errorMessage ? (
        <>
          <Text className="mb-4 text-center" type="h3">
            <Trans>Link expired</Trans>
          </Text>
          <Text className="colorWhiteToBlack my-2 text-center">{errorMessage}</Text>
          <Button
            className="mt-4"
            onClick={() => {
              window.location.replace('/login')
            }}
          >
            <span className="colorWhite">
              <Trans>Back to login</Trans>
            </span>
          </Button>
        </>
      ) : (
        !isLogged &&
        !emailSent && (
          <>
            <Text className="mb-2 text-center" type="h3">
              {isSignUp ? 'Join DataHive AI' : 'Welcome'}
            </Text>
            <Text className="mb-4 text-center !text-[var(--colorGray70to100)]">
              {isSignUp ? 'Sign up to start earning!' : 'Log in to start earning!'}
            </Text>
            <Button onClick={() => googleLogin()} className="mt-3">
              <span className="flex justify-center gap-2">
                <GoogleIcon />
                Continue with Google
              </span>
            </Button>
            <Text className="colorWhiteToBlack my-2 text-center">
              <Trans>or</Trans>
            </Text>
            <Input
              className={`colorWhiteToBlack w-full max-w-sm rounded-xl ${!isEmailValid && !isEmailPristine && 'borderColorError colorError'}`}
              ref={inputRef}
              placeholder={t`Email`}
              value={email}
              onChange={(value) => setEmail(value)}
              onKeyEnter={handleEmailLogin}
              isError={!isEmailValid && !isEmailPristine}
              errorText={'Please enter a valid email address'}
            />
            <Button
              variant="bordered"
              isLoading={isEmailLoading}
              className="mt-4"
              onClick={() => handleEmailLogin(email)}
            >
              <span className="colorWhiteToBlack">{isSignUp ? 'Sign up with email' : 'Login with email'}</span>
            </Button>
          </>
        )
      )}
      {emailSent && (
        <>
          <Text className="mb-4 text-center" type="h3">
            <Trans>Email sent</Trans>
          </Text>
          <Text className="colorWhiteToBlack my-2 text-center">
            <Trans>Please check your inbox and follow the link to proceed. The link will expire in 15 minutes.</Trans>
          </Text>
          <Button className="mt-4" onClick={() => setEmailSent(false)}>
            <span className="colorWhite">
              <Trans>Back to login</Trans>
            </span>
          </Button>
        </>
      )}
    </>
  )
}

export default LoginPage
